import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import colors from '@honeybook/hbui/dist/styles/colors';
import HBTabs from '@honeybook/hbui/dist/components/HBTabs/HBTabs';
import DesktopComputer24 from '@honeybook/icons/dist/icons/DesktopComputer24';
import MobilePhone from '@honeybook/icons/dist/icons/MobilePhone24';
import { Tab } from '@honeybook/hbui/dist/components/HBTabs/HBTabs.styles';
import { TPreviewTabId } from '~/types/builder.types';
import { setIconFillColor } from '@honeybook/hbui/dist/styles/mixins';
import { isNot } from 'styled-is';

interface Props {
    selectedTabId: string;
    onClickTab: (tabId: TPreviewTabId) => void;
    shouldShowPreviewOfTestVersion: boolean;
}

const PreviewTypeTabs = ({
    selectedTabId,
    onClickTab,
    shouldShowPreviewOfTestVersion
}: Props) => {
    const handleClick = (tabId: TPreviewTabId) => {
        onClickTab(tabId);
    };

    const getColorForIcon = useCallback(
        view =>
            shouldShowPreviewOfTestVersion && selectedTabId === view
                ? colors.neutral900
                : colors.neutral500,
        [selectedTabId, shouldShowPreviewOfTestVersion]
    );

    const optionalViewTabs = useMemo(() => {
        return [
            {
                id: 'Desktop',
                icon: <DesktopComputer24 color={getColorForIcon('Desktop')} />
            },
            {
                id: 'Mobile',
                icon: <MobilePhone color={getColorForIcon('Mobile')} />
            }
        ];
    }, [getColorForIcon]);

    return (
        <Tabs
            testIdName="preview"
            noShadow
            stretchToFill
            selectedHighlightColor={colors.purpleHeart}
            tabs={optionalViewTabs}
            selectedTab={selectedTabId}
            onClick={handleClick}
            shouldShowPreviewOfTestVersion={shouldShowPreviewOfTestVersion}
        />
    );
};

export default observer(PreviewTypeTabs);

// -------------------------------- Styles -------------------------------- //
const Tabs = styled(HBTabs)<{ shouldShowPreviewOfTestVersion: boolean }>`
    border-bottom: 0;
    padding-top: 18px;
    position: absolute;
    left: calc(50% + 6px);
    top: 50%;
    transform: translate(-50%, -50%);
    ${isNot('shouldShowPreviewOfTestVersion')`
        ${Tab} {
         ${setIconFillColor(colors.neutral500)}
        }
    `}
`;
